import React, { useEffect, useState } from 'react';
import { Combine, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbInwardApi } from '@/services/ewayBillApi';
import { GstinTag } from '@/components/ewayBill/SupplierTable';

/** Admin: keep one of the ticked suppliers; the others are removed and their saved bills move to it. */
export const MergeSuppliersDialog = ({ rows, open, onClose, onMerged }) => {
  const [keep, setKeep] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setKeep([...rows].sort((a, b) => (b.used_count || 0) - (a.used_count || 0))[0]?.id || null); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const merge = () => {
    setBusy(true);
    ewbInwardApi.mergeSuppliers({ keep_id: keep, merge_ids: rows.map((r) => r.id).filter((id) => id !== keep) })
      .then((r) => { toast.success(`Merged ${r.merged} into ${r.kept.legal_name} — ${r.bills_updated} saved bill(s) updated${r.bills_kept ? `, ${r.bills_kept} with an e-way bill kept as sent` : ''}`); onMerged(); })
      .catch((e) => toast.error(errMsg(e, 'Merge failed'), { duration: 8000 })).finally(() => setBusy(false));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="merge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Combine className="w-5 h-5" />Merge {rows.length} suppliers</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Pick the one to keep. The others are removed; their saved inward bills move to it, and the ones without an e-way bill take its details.</p>
        <div className="divide-y rounded-lg border">
          {rows.map((s) => (
            <label key={s.id} className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm ${keep === s.id ? 'bg-emerald-50' : 'hover:bg-slate-50'}`} data-testid={`merge-keep-${s.id}`}>
              <input type="radio" name="keep" checked={keep === s.id} onChange={() => setKeep(s.id)} className="accent-emerald-700" />
              <span className="font-semibold">{s.legal_name}</span><GstinTag gstin={s.gstin} />
              <span className="ml-auto text-xs text-gray-500">{s.place} – {s.pincode} · {s.used_count || 0} bill(s)</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="merge-cancel-btn">Cancel</Button>
          <Button onClick={merge} disabled={busy || !keep} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="merge-confirm-btn">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Merge into the kept one</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MergeSuppliersDialog;
